import { useNavigation } from '@react-navigation/native'
import { PostTypes } from 'constants/postTypes'
import { useTheme } from 'contexts'
import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { RefreshControl } from 'react-native'

import { EmptyState } from 'partials/EmptyState'

import { Box } from 'widgets/Box'
import { ScrollableTabView } from 'widgets/TabView'

import { useCurrentUser } from 'hooks/useCurrentUser'
import { useInfiniteBookmarks } from 'hooks/useInfiniteBookmarks'
import { useInfinitePosts } from 'hooks/useInfinitePosts'
import { useQueryClient } from 'hooks/useQueryClient'
import { useUserCreatedEvents } from 'hooks/useUserCreatedEvents'

import { links } from 'utils/constants'

import { Post } from 'types/post'
import { UserType } from 'types/user'

import { ProfileHeader } from './ProfileHeader'

import { EventList } from '../EventList'
import { PostsGridView } from '../UpspaceTabs/widgets/PostsGridView'

type UserProfilePreviewProps = {
  user?: UserType
  setIsCommentLoading: (isCommentLoading: boolean) => void
}

export const UserProfilePreview = ({ user, setIsCommentLoading }: UserProfilePreviewProps) => {
  const theme = useTheme()
  const queryClient = useQueryClient()
  const { data: currentUser } = useCurrentUser()
  const isCurrentUser = useMemo(() => currentUser?.id === user?.id, [currentUser?.id, user?.id])

  const navigation = useNavigation<any>()

  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false)

  const {
    data: eventsData,
    isLoading: isEventsLoading,
    fetchNextPage: fetchNextUserCreatedEvents,
    isFetchingNextPage: isFetchingNextEvents,
  } = useUserCreatedEvents({ userId: user?.id })

  const fetchNextEvents = () => {
    setIsFetchingNextPage(true)
    fetchNextUserCreatedEvents()
  }

  const events = useMemo(
    () => eventsData?.pages.reduce((acc, page) => acc.concat(page), []) ?? [],
    [eventsData?.pages],
  )

  const userEvents = useMemo(() => {
    let finalEvents = events.filter(
      (event) =>
        (event.eventType === 'create' || event.eventType === 'completion') &&
        !event.post?.isArchived &&
        !event.post?.isPending,
    )

    const uniqueIds: string[] = []
    finalEvents = finalEvents.filter((event) => {
      const isDuplicate = uniqueIds.includes(event.id)

      if (!isDuplicate) {
        uniqueIds.push(event.id)

        return true
      }

      return false
    })

    finalEvents.sort((a, b) => {
      return b.created - a.created
    })

    setIsFetchingNextPage(false)
    return finalEvents
  }, [events])

  const {
    data: posts,
    isLoading: isPostsLoading,
    fetchNextPage: fetchNextPosts,
  } = useInfinitePosts({
    creatorId: user?.id,
    shouldIncludeDrafts: isCurrentUser,
  })

  const filteredPosts = useRef<Array<Post>>([])

  useEffect(() => {
    const newFilteredPosts = posts
      .filter(
        (p) =>
          (p.isPending === false || p.isPending === undefined) &&
          (p.isArchived === false || p.isArchived === undefined),
      )
      .filter((item) => item.hasSteps || item.type === PostTypes.WORKOUT)

    filteredPosts.current = newFilteredPosts
    // setIsFetchingNextPage(false)
  }, [posts, isPostsLoading])

  const {
    data: bookmarks,
    isLoading: isBookmarksLoading,
    fetchNextPage: fetchNextPageBookmarks,
    isFetchingNextPage: isFetchingNextBookmarks,
  } = useInfiniteBookmarks({
    userId: user?.id,
  })
  const bookmarkedPosts = useRef<Array<Post>>(bookmarks.map((bookmark) => bookmark.post))

  useEffect(() => {
    const newBookmarkedPosts = bookmarks?.map((bookmark) => bookmark.post)
    bookmarkedPosts.current = newBookmarkedPosts
  }, [bookmarks, isBookmarksLoading])

  const isProfileRefreshing = useMemo(
    () =>
      (isBookmarksLoading && !isFetchingNextBookmarks) ||
      (isPostsLoading && !isFetchingNextPage) ||
      (isEventsLoading && !isFetchingNextEvents),
    [
      isBookmarksLoading,
      isEventsLoading,
      isFetchingNextBookmarks,
      isFetchingNextEvents,
      isFetchingNextPage,
      isPostsLoading,
    ],
  )

  const onProfileRefresh = useCallback(() => {
    queryClient.invalidateQueries(['userCreatedEvents', user?.id])
    queryClient.invalidateQueries([
      'posts',
      user?.id,
      undefined,
      'created',
      isCurrentUser,
      10,
      undefined,
    ])
    queryClient.invalidateQueries([['bookmarks', user?.id, 'created', 10]])
  }, [isCurrentUser, queryClient, user?.id])

  if (!user) return null

  const navigateToWorkoutsPage = () => {
    navigation.navigate(links.WORKOUTS_PAGE)
  }

  const hasFeedContent = userEvents && userEvents?.length
  const hasGalleryContent = filteredPosts?.current && filteredPosts?.current?.length
  const hasBookmarksContent = bookmarkedPosts?.current && bookmarkedPosts?.current?.length

  const feedTab = {
    Feed: (
      <>
        {!hasFeedContent ? (
          <Box width='100%' height={250}>
            <EmptyState
              title='Media'
              subtitle='Nothing to see here...'
              subtitle2='Click the + sign below to create your first post or workout.'
            />
          </Box>
        ) : (
          <EventList
            events={userEvents}
            fetchNextEvents={fetchNextEvents}
            loading={isEventsLoading}
            noContentMessage={
              isCurrentUser
                ? 'You have no activity yet.'
                : 'There has been no activity by this user yet.'
            }
            setIsCommentLoading={setIsCommentLoading}
            isFetchingNextPage={isFetchingNextPage}
          />
        )}
      </>
    ),
  }

  const gridTab = {
    Grid: (
      <>
        {!hasGalleryContent ? (
          <Box width='100%' height={250}>
            <EmptyState
              title='Media'
              subtitle='Nothing to see here...'
              subtitle2='Click the + sign below to create your first post or workout.'
            />
          </Box>
        ) : (
          <PostsGridView
            isLoading={isPostsLoading}
            posts={filteredPosts.current}
            columns={3}
            onEndReached={fetchNextPosts}
          />
        )}
      </>
    ),
  }

  const bookmarksTab = {
    Bookmarks: (
      <>
        {!hasBookmarksContent ? (
          <Box width='100%' height={250}>
            <EmptyState
              title='Bookmarks'
              subtitle="Let's put some meat on your bones"
              inBetweenMargin='md'
              actionButtons={[
                {
                  title: 'Discover Workouts',
                  type: 'primary',
                  onPress: navigateToWorkoutsPage,
                },
              ]}
            />
          </Box>
        ) : (
          <PostsGridView
            isLoading={isEventsLoading}
            posts={bookmarkedPosts.current}
            onEndReached={fetchNextPageBookmarks}
            columns={3}
          />
        )}
      </>
    ),
  }

  const tabMap: Record<string, ReactNode> = isCurrentUser
    ? { ...feedTab, ...gridTab, ...bookmarksTab }
    : { ...feedTab, ...gridTab }

  return (
    <ScrollableTabView
      withIconTabHeaders
      refreshControl={
        <RefreshControl
          refreshing={isProfileRefreshing}
          onRefresh={onProfileRefresh}
          progressBackgroundColor={theme.colors.v2primary}
          tintColor={theme.colors.v2primary}
        />
      }
      tabs={Object.keys(tabMap)}
      header={<ProfileHeader user={user} />}
    >
      {Object.values(tabMap)}
    </ScrollableTabView>
  )
}
